import { useEffect, useState, type JSX, type ReactNode } from 'react';
import { Link } from 'react-router';
import { usePostHog } from '@posthog/react';
import {
  Badge,
  Button,
  EmptyState,
  Search,
  Select,
  SelectItem
} from '@viniciomendesr/rayrovisk-design-system';
import { useMe, usePrompts } from '../api/queries';
import { AccessStateScreen, accessStateOf } from '../access/AccessState';
import { categoryShortLabel } from './categories';
import type { Category, Prompt } from '../api/types';

type Order = 'curados' | 'novos' | 'az';

/** How many rows the list shows before "ver mais". The design's own default. */
const PAGE = 8;

/** Longer than a keystroke, shorter than a thought. Only analytics reads it. */
const SEARCH_SETTLE_MS = 700;

/**
 * The order the chips are drawn in, which is the design's and not the route's.
 *
 * The API sorts categories alphabetically by slug, which interleaves the shelves
 * by accident: "Carrossel e post" lands before "Ganchos e abertura" because `c`
 * precedes `g`. The design orders them the way the work happens instead, from
 * the hook through the formats to what gets paid for, so the row reads as a
 * sequence rather than as a dictionary.
 *
 * A slug the API sends that is missing here still renders, after the pinned
 * ones: a category added to the catalogue tomorrow must not vanish from the
 * filter row just because nobody has placed it yet.
 */
const CHIP_ORDER = [
  'gancho-3-segundos',
  'roteiro-reels',
  'carrossel-estatico',
  'stories-sequencias',
  'legenda-cta-microcopy',
  'diagnostico-voz',
  'pauta-ideacao',
  'reaproveitamento',
  'monetizacao'
];

function orderCategories(categories: Category[]): Category[] {
  const rank = new Map(CHIP_ORDER.map((slug, index) => [slug, index]));
  // `sort` is stable, so unplaced slugs keep the order the route sent them in
  // rather than shuffling between renders.
  return [...categories].sort(
    (a, b) =>
      (rank.get(a.name) ?? Number.MAX_SAFE_INTEGER) -
      (rank.get(b.name) ?? Number.MAX_SAFE_INTEGER)
  );
}

/**
 * The chips under "Tente:", in the design's wording and the design's order.
 *
 * They are seed queries, not decoration, so each one has to return something
 * against the real catalogue: a chip that lands on the fallback path teaches
 * her that the search does not work.
 *
 * That is asserted rather than asserted-in-a-comment — the catalogue grows,
 * and a comment quoting hit counts is wrong by the next batch. The service's
 * `catalogue.test.ts` runs these five strings through `searchCatalog` and
 * fails if any of them falls back. It holds a copy of the list, because the
 * service cannot import the web app; changing a seed here means changing it
 * there.
 *
 * They are phrased the way she would say it out loud rather than the way the
 * catalogue files it, which is the whole argument of this screen.
 */
const SEEDS = [
  'gancho de Reels',
  'legenda que vende',
  'não sei o que postar',
  'carrossel tutorial',
  'responder uma marca'
] as const;

/**
 * Whether she has waved the invitation away.
 *
 * This is a preference about appearance, not a security fact. Nothing about
 * having a password is stored here — the server answers that on every load, and
 * a browser that lies about this key changes what she is offered and nothing
 * she can reach. Which is also why `localStorage` is enough: the worst case is
 * a card she already dismissed reappearing on another device.
 */
const DISMISSED_KEY = 'roteirovisk.password-invite-dismissed';

function readDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    // Safari in private mode throws rather than returning null. An invitation
    // is not worth taking the library down for.
    return false;
  }
}

/**
 * ADR-0019's second door had a screen and a route and no way in: nothing after
 * the first sign-in ever mentioned it, so the opt-in was opt-in to a page nobody
 * could find. This is the mention.
 *
 * It sits above the library rather than in a settings screen because the person
 * who needs it most is the one who opens the product every week and waits on an
 * email each time.
 */
function PasswordInvite(): JSX.Element | null {
  const me = useMe();
  const posthog = usePostHog();
  // Read once, on mount. An effect that re-reads would run again on every route
  // change for a value that only this component's own button can alter.
  const [dismissed, setDismissed] = useState(readDismissed);

  // `me.data` gates it, so nothing is drawn before the answer arrives. Rendering
  // first and hiding on arrival is how a card flashes on every page load.
  if (dismissed || !me.data || me.data.hasPassword) {
    return null;
  }

  function dismiss() {
    setDismissed(true);
    posthog?.capture('password_invite_dismissed');
    try {
      window.localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // Dismissed for this session either way. Storage is how it lasts, not how
      // it works.
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 'clamp(8px, 2.5vw, 20px)',
        padding: 'clamp(10px, 3vw, 16px)',
        background: 'var(--rvk-surface-1)',
        border: '1px solid var(--rvk-border-subtle)',
        borderLeft: '3px solid var(--rvk-accent-default)',
        borderRadius: 'var(--rvk-radius-md)'
      }}
    >
      {/* min-width lets it share a row on a wide screen and take the whole one
          on a phone, without a second breakpoint deciding the same thing. */}
      <div style={{ flex: '1 1 260px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Two short lines, and they are short on purpose: at 320px a third one
            costs 20px of a 568px screen, and this card is an offer sitting on
            top of what she actually came for. */}
        <h2
          style={{
            margin: 0,
            font: '400 16px/1.25 var(--rvk-font-expressive)',
            textWrap: 'balance'
          }}
        >
          Entrar sem esperar o e-mail
        </h2>
        <p
          style={{
            margin: 0,
            font: '400 13px/1.5 var(--rvk-font-functional)',
            color: 'var(--rvk-text-secondary)'
          }}
        >
          A senha é opcional, e o link por e-mail continua funcionando.
        </p>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Link to="/senha/definir">
          <Button variant="secondary" size="sm" type="button">
            Definir senha
          </Button>
        </Link>
        <Button variant="ghost" size="sm" type="button" onClick={dismiss}>
          Agora não
        </Button>
      </div>
    </div>
  );
}

/**
 * A filter chip, hand-rolled rather than the design system's `Tag`.
 *
 * `Tag selectable` is the right component by shape: it renders a real button
 * and carries `aria-pressed`, which is what a toggle owes assistive tech. Two
 * things stop it being the right one here.
 *
 * The first is the pairing. `Tag`'s selected state draws `--rvk-accent-subtle`
 * under an `--rvk-accent-default` border, and the token that has to carry this
 * boundary is `--rvk-state-selected-ring`: only the ring comes with the "always
 * ≥3:1 against its own surface" guarantee, in every brand × theme, which is the
 * finding recorded at length on the category card this row replaces (and which
 * the two contrast tests in `LibraryScreen.test.tsx` pin). A tinted fill needs
 * a partner that clears WCAG 1.4.11 on its own; accent-default is not promised
 * to be one.
 *
 * The second is 360px. `Tag` clips its label with `text-overflow: ellipsis` and
 * `white-space: nowrap`, and "Reaproveitar o gravado" beside a count is exactly
 * the string that loses its last word on a phone.
 *
 * Note what is *not* different: no hex, no copied token value, and the same
 * `accent-subtle` + `state-selected-ring` pair the card used. This is the same
 * decision in a new shape, not a new decision.
 */
function Chip({
  children,
  count,
  pressed,
  onClick
}: {
  children: ReactNode;
  /**
   * Rendered by the chip rather than passed in as part of `children`, so the
   * space before it is never left to JSX. Written as a sibling text node the
   * label and the number run together in the accessible name ("Todos7"), and
   * flexbox drops a whitespace-only item without rendering it, so the separator
   * costs nothing on screen and fixes what a screen reader says.
   */
  count?: number;
  /** Omitted for the seed chips: they run a search, they do not hold a state. */
  pressed?: boolean;
  onClick: () => void;
}): JSX.Element {
  const selected = pressed === true;
  return (
    <button
      type="button"
      className="rvk-focus-ring"
      aria-pressed={pressed}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        // Comfortably past the 24px minimum without the invisible 44px overlay
        // the design system's own chip uses: this row wraps onto several lines
        // and an overlay that tall would reach into the line above it.
        minHeight: 36,
        padding: '7px 14px',
        cursor: 'pointer',
        textAlign: 'left',
        background: selected ? 'var(--rvk-accent-subtle)' : 'var(--rvk-surface-1)',
        color: 'var(--rvk-text-primary)',
        border: `1px solid ${selected ? 'var(--rvk-state-selected-ring)' : 'var(--rvk-border-subtle)'}`,
        borderRadius: 'var(--rvk-radius-full)',
        font: `${selected ? 500 : 400} 13px/1.3 var(--rvk-font-functional)`
      }}
    >
      {children}
      {count !== undefined && (
        <>
          {' '}
          {/* Tabular figures so the pills do not jitter as counts change.
              The colour steps up on the selected chip, and it is not cosmetic:
              `text-tertiary` reads 5.21:1 on `surface-1`, but the same tertiary
              on the `accent-subtle` tint measures 3.28:1 in the browser, which
              a 12px number is not allowed to be (WCAG 1.4.3 wants 4.5:1). This
              is invisible to jsdom, which has no colours to measure.
              `text-secondary` on the tint measures 5.96:1 and still sits a step
              below the 10.56:1 label, so the two levels survive selection. */}
          <span
            style={{
              font: '400 12px/1.2 var(--rvk-font-functional)',
              fontVariantNumeric: 'tabular-nums',
              color: selected ? 'var(--rvk-text-secondary)' : 'var(--rvk-text-tertiary)'
            }}
          >
            {count}
          </span>
        </>
      )}
    </button>
  );
}

/**
 * What the next step of the ladder adds, for someone standing on the cheap one.
 *
 * D4, and the shape matters more than the copy: **not a locked grid.** A wall
 * of padlocks is a paywall wearing a library's clothes, and this product has
 * already decided that what she does not own stays listed with a way to buy
 * rather than teased. So her twenty are a whole library that behaves like one,
 * and this is a single sentence underneath it.
 *
 * Both numbers come off the response. The client has no idea how big the
 * library is until the server says so, which is the point: a screen that
 * guessed would be guessing about a claim.
 */
function NextStep({ holds, library }: { holds: number; library: number }): JSX.Element | null {
  const me = useMe();
  const products = me.data?.products;

  // Drawn only once the answer is in, and only for someone the offer is for.
  // A pack buyer already holds all of it; rendering first and hiding on arrival
  // is how a card flashes on every page load.
  //
  // `Number.isFinite` rather than a truthiness check: an older service that
  // does not send the field at all would make `library - holds` render as NaN,
  // and "abre os outros NaN" is the kind of thing a suite never sees because
  // every fixture happens to hold the pack.
  if (
    !products ||
    products['prompt-pack'] === 'granted' ||
    !Number.isFinite(library) ||
    library <= holds
  ) {
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 'clamp(8px, 2.5vw, 20px)',
        padding: 'clamp(10px, 3vw, 16px)',
        background: 'var(--rvk-surface-1)',
        border: '1px solid var(--rvk-border-subtle)',
        borderLeft: '3px solid var(--rvk-accent-default)',
        borderRadius: 'var(--rvk-radius-md)'
      }}
    >
      <p
        style={{
          flex: '1 1 260px',
          minWidth: 0,
          margin: 0,
          font: '400 13.5px/1.5 var(--rvk-font-functional)',
          color: 'var(--rvk-text-secondary)'
        }}
      >
        Você tem {holds} prompts. O pacote completo abre os outros {library - holds}, e o que entrar
        depois continua chegando.
      </p>
      <a href="/" rel="noreferrer">
        <Button variant="secondary" size="sm" type="button">
          Ver o pacote completo
        </Button>
      </a>
    </div>
  );
}

export function LibraryScreen(): JSX.Element {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [order, setOrder] = useState<Order>('curados');
  const [expanded, setExpanded] = useState(false);
  const posthog = usePostHog();

  const prompts = usePrompts(query, category);
  const state = accessStateOf(prompts.error);

  /**
   * One event when the search settles, not one per keystroke.
   *
   * The field searches on every change by design, so capturing inside the
   * handler sends `c`, `ca`, `car`… — a report of "most searched terms" made
   * almost entirely of one-letter prefixes, paid for by the event.
   *
   * It carries the shape of the search and never the words. A prompt library's
   * search box is free text a person typed, and ADR-0022 keeps that out of a
   * third party; the number worth having is `result_count: 0`, the searches the
   * catalogue could not answer.
   */
  useEffect(() => {
    const typed = query.trim();
    if (typed === '') {
      return;
    }

    const timer = setTimeout(() => {
      posthog?.capture('library_searched', {
        query_length: typed.length,
        result_count: prompts.data?.prompts.length ?? 0,
        fell_back: prompts.data?.fellBackToSuggestions ?? false,
        filtered_by_category: category !== null
      });
    }, SEARCH_SETTLE_MS);

    return () => clearTimeout(timer);
  }, [query, category, prompts.data, posthog]);

  if (state) {
    return <AccessStateScreen state={state} />;
  }

  const data = prompts.data;
  const all = data?.prompts ?? [];
  /** Every match the service returned. The page is a slice of this, never this. */
  const found = sort(all, order);
  const shown = expanded ? found : found.slice(0, PAGE);
  const categories = data ? orderCategories(data.categories) : [];

  /**
   * Every number on this screen is read off the response, never off a constant.
   * `categories` counts the slice her tier opens, whole rather than filtered —
   * the route sends it regardless of `q` and `category` — so this is what she
   * holds, not what the current search matched. Printing a total the catalogue
   * cannot serve her is publicidade enganosa (CDC art. 37).
   *
   * The design writes this denominator as the literal `100`. That is the one
   * thing in it that must not be copied: the catalogue was 100 when the design
   * was drawn, is larger now, and the promise (PP-12) is that it keeps growing.
   */
  const total = data ? countAll(data.categories) : 0;

  /**
   * The denominator under a category filter, from the same response.
   *
   * Falling back to `found.length` rather than to zero if a slug is somehow
   * absent: "18 de 18" is merely uninformative, while "18 de 0" is a false
   * statement about what she owns.
   */
  const inCategory =
    category === null
      ? null
      : (data?.categories.find((item) => item.name === category)?.count ?? found.length);

  function runSearch(next: string) {
    setQuery(next);
    setExpanded(false);
  }

  function chooseCategory(next: string | null) {
    setCategory(next);
    setExpanded(false);
  }

  /** The way out of the empty state: drops both filters, not just the typed one. */
  function clearEverything() {
    setQuery('');
    setCategory(null);
    setExpanded(false);
  }

  const filtering = query.trim() !== '' || category !== null;
  const plural = (n: number) => (n === 1 ? 'prompt' : 'prompts');

  /**
   * The heading names the list when nothing is filtered and counts it when
   * something is. `0 prompts para isso` is the zero case of that one rule, not
   * a state of its own.
   */
  const listTitle = filtering
    ? `${found.length} ${plural(found.length)} para isso`
    : 'Biblioteca de Prompts';

  /**
   * The meta line has to say which of two totals it is quoting. The chip counts
   * describe the whole catalogue, so under a category filter the denominator is
   * that category and the noun changes with it — otherwise "18 de 120" sits
   * directly under a chip reading "18" and the two contradict each other.
   */
  const listMeta =
    category === null
      ? `${found.length} de ${total} no seu pacote`
      : `${found.length} de ${inCategory} nesta faixa`;

  const remaining = found.length - PAGE;

  // Only a genuinely empty response draws the panel. On the fallback path the
  // service answers with the whole pool, so this stays false and she gets rows.
  const nothingToShow = !prompts.isLoading && Boolean(data) && found.length === 0;

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 26, maxWidth: 1000 }}>
      <PasswordInvite />

      {/* The screen asks what she came to do instead of naming its own
          furniture. "Biblioteca de Prompts" is still on the page, further down,
          where it labels the result list rather than the visit. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <h1 style={{ margin: 0, font: '400 32px/1.12 var(--rvk-font-expressive)', textWrap: 'balance' }}>
          O que você vai escrever hoje?
        </h1>
        <p
          style={{
            margin: 0,
            font: '400 14.5px/1.6 var(--rvk-font-functional)',
            color: 'var(--rvk-text-secondary)'
          }}
        >
          Insira qual a sua tarefa e filtre os melhores prompts
        </p>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          padding: 'clamp(12px, 3vw, 16px)',
          background: 'var(--rvk-surface-1)',
          border: '1px solid var(--rvk-border-subtle)',
          borderRadius: 'var(--rvk-radius-md)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {/* `Search` brings its own magnifier and its own clear affordance, and
              the clear only appears once there is something to clear. */}
          <div style={{ flex: '1 1 260px', minWidth: 0 }}>
            <Search
              label="Buscar pela tarefa"
              size="lg"
              /* A sentence in her words, not a list of nouns. The old
                 "gancho, legenda, finalização…" asked her to know the
                 catalogue's vocabulary before she could use it. */
              placeholder="ex.: ninguém para no meu vídeo"
              value={query}
              onChange={(event) => runSearch(event.target.value)}
              onClear={() => runSearch('')}
            />
          </div>
          {/* Fixed rather than flexible: the search grows into whatever is left,
              which is what puts this at the right edge without a margin hack. */}
          <div style={{ flex: '0 0 176px' }}>
            <Select
              aria-label="Ordenar a biblioteca"
              value={order}
              onValueChange={(value) => setOrder(value as Order)}
            >
              <SelectItem value="curados">Recomendados</SelectItem>
              <SelectItem value="novos">Novos primeiro</SelectItem>
              <SelectItem value="az">A a Z</SelectItem>
            </Select>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span
            style={{
              font: '400 12.5px/1.4 var(--rvk-font-functional)',
              color: 'var(--rvk-text-secondary)'
            }}
          >
            Tente:
          </span>
          {SEEDS.map((seed) => (
            <Chip key={seed} onClick={() => runSearch(seed)}>
              {seed}
            </Chip>
          ))}
        </div>
      </div>

      {data && categories.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Written in sentence case and uppercased by CSS, so a screen reader
              says the words instead of spelling out an acronym. */}
          <span
            style={{
              font: '500 11px/1.4 var(--rvk-font-functional)',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--rvk-text-tertiary)'
            }}
          >
            Ou filtre por assunto
          </span>
          <div
            role="group"
            aria-label="Filtrar por assunto"
            style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
          >
            <Chip count={total} pressed={category === null} onClick={() => chooseCategory(null)}>
              Todos
            </Chip>
            {categories.map((item) => {
              const active = category === item.name;
              return (
                <Chip
                  key={item.name}
                  count={item.count}
                  pressed={active}
                  /* Tapping the chip that is already on clears it: the row is
                     the filter, so it has to be able to undo itself. */
                  onClick={() => chooseCategory(active ? null : item.name)}
                >
                  {categoryShortLabel(item.name)}
                </Chip>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 10,
            flexWrap: 'wrap'
          }}
        >
          <h2 style={{ margin: 0, font: '400 19px/1.2 var(--rvk-font-expressive)' }}>{listTitle}</h2>
          {data && (
            <span
              style={{
                font: '400 12.5px/1.4 var(--rvk-font-functional)',
                color: 'var(--rvk-text-secondary)'
              }}
            >
              {listMeta}
            </span>
          )}
        </div>

        {data && <NextStep holds={total} library={data.libraryTotal} />}

        {prompts.isLoading && (
          <p style={{ font: '400 13px/1.5 var(--rvk-font-functional)', color: 'var(--rvk-text-tertiary)' }}>
            Carregando a sua biblioteca…
          </p>
        )}

        {/* PP-4, the half the mockup has no state for.
            `searchCatalog` answers a miss with the whole pool rather than with
            nothing, so the common "nothing matched" case arrives here as a full
            list — and a full list with no explanation reads as a search that
            ignored her. The notice says what happened and leaves the rows where
            they are; the empty panel below never draws in this case. */}
        {data?.fellBackToSuggestions && found.length > 0 && (
          <p
            style={{
              margin: 0,
              padding: '10px 14px',
              font: '400 13px/1.5 var(--rvk-font-functional)',
              color: 'var(--rvk-text-secondary)',
              background: 'var(--rvk-surface-1)',
              border: '1px solid var(--rvk-border-subtle)',
              borderLeft: '3px solid var(--rvk-accent-default)',
              borderRadius: 'var(--rvk-radius-md)'
            }}
          >
            Nada bateu exatamente com “{query}”. Estes são os prompts que você já tem; filtre por
            assunto para chegar mais perto.
          </p>
        )}

        {found.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              background: 'var(--rvk-surface-1)',
              border: '1px solid var(--rvk-border-subtle)',
              borderRadius: 'var(--rvk-radius-md)',
              overflow: 'hidden'
            }}
          >
            {shown.map((prompt, index) => (
              <Link
                key={prompt.slug}
                to={`/biblioteca/${prompt.slug}`}
                className="rvk-library-row"
                style={{
                  display: 'flex',
                  // `align-items` is in app.css, not here: it changes at the
                  // 560px breakpoint, and an inline value would outrank the
                  // media query that has to change it.
                  justifyContent: 'space-between',
                  gap: 16,
                  textAlign: 'left',
                  padding: 16,
                  color: 'var(--rvk-text-primary)',
                  borderBottom:
                    index === shown.length - 1 ? undefined : '1px solid var(--rvk-border-subtle)'
                }}
              >
                <span style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
                  {/* The hierarchy is inverted against what shipped before, and
                      that inversion is the point of the redesign: she arrives
                      holding a situation, not a title. "Quando o vídeo tem boa
                      retenção depois do início…" is the line she can match
                      herself against; the title is the name of the tool that
                      answers it, which is only useful once she has matched. */}
                  <span
                    style={{
                      font: '600 15px/1.5 var(--rvk-font-functional)',
                      color: 'var(--rvk-text-primary)'
                    }}
                  >
                    {prompt.whenToUse}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span
                      style={{
                        font: '400 12.5px/1.4 var(--rvk-font-functional)',
                        color: 'var(--rvk-text-secondary)'
                      }}
                    >
                      {prompt.title}
                    </span>
                    {/* On every row, including inside a filtered list. It costs
                        a repetition there and it buys one rule instead of two,
                        which is what the design asked for. */}
                    <Badge tone="neutral" variant="outline" size="sm">
                      {categoryShortLabel(prompt.category)}
                    </Badge>
                  </span>
                </span>
                {/* A span, not a button. The whole row is the link — nesting a
                    real button inside an anchor is invalid and gives keyboard
                    users two stops for one destination — so this is the
                    affordance drawn, and hidden from assistive tech because the
                    link it sits in already says where it goes. */}
                <span
                  aria-hidden="true"
                  className="rvk-library-cta"
                  style={{
                    flex: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 14px',
                    font: '500 12.5px/1.2 var(--rvk-font-functional)',
                    color: 'var(--rvk-text-primary)',
                    border: '1px solid var(--rvk-border-default)',
                    borderRadius: 'var(--rvk-radius-md)'
                  }}
                >
                  Ver o prompt
                </span>
              </Link>
            ))}
          </div>
        )}

        {/* The package's own component, which already is this panel: `framed`
            draws the dashed border, and `reason="no-results"` supplies the
            magnifier in a circular tile. Only the words are ours.

            PP-4 rides in the `actions` slot rather than beside it. That slot
            takes an arbitrary node, so when the service named nearby subjects
            they go in as taps above the way out, introduced by a line: a person
            who just paid never gets a screen that offers her nothing. With no
            suggestions the slot holds the single button the design specifies. */}
        {nothingToShow && (
          <EmptyState
            reason="no-results"
            title="Nada bateu com essa busca."
            description="Tente uma palavra que descreva o momento, como “ninguém para no meu vídeo” ou “responder uma marca”. Ou limpe a busca e filtre por assunto."
            actions={
              data?.fellBackToSuggestions && data.suggestedCategories.length > 0 ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 12
                  }}
                >
                  <span
                    style={{
                      font: '400 13px/1.5 var(--rvk-font-functional)',
                      color: 'var(--rvk-text-secondary)'
                    }}
                  >
                    Ou comece por um destes assuntos:
                  </span>
                  <div
                    style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}
                  >
                    {data.suggestedCategories.map((name) => (
                      <Chip
                        key={name}
                        onClick={() => {
                          chooseCategory(name);
                          setQuery('');
                        }}
                      >
                        {categoryShortLabel(name)}
                      </Chip>
                    ))}
                  </div>
                  <Button variant="secondary" size="sm" type="button" onClick={clearEverything}>
                    Limpar a busca
                  </Button>
                </div>
              ) : (
                <Button variant="secondary" size="sm" type="button" onClick={clearEverything}>
                  Limpar a busca
                </Button>
              )
            }
          />
        )}

        {/* Centred, and it counts what is still hidden rather than restating the
            total: "Ver mais 39 prompts" answers the question the row under it
            actually raises. The meta line above already carries the total. */}
        {found.length > PAGE && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 4 }}>
            <Button variant="secondary" size="sm" onClick={() => setExpanded(!expanded)}>
              {expanded ? 'Mostrar menos' : `Ver mais ${remaining} ${plural(remaining)}`}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}

function countAll(categories: Array<{ count: number }>): number {
  return categories.reduce((total, item) => total + item.count, 0);
}

/**
 * "Recomendados" is the order the service sent, which is the search
 * relevance order — re-sorting it here would throw away the ranking.
 */
function sort(prompts: Prompt[], order: Order): Prompt[] {
  if (order === 'curados') {
    return prompts;
  }
  const copy = [...prompts];
  if (order === 'az') {
    return copy.sort((a, b) => a.title.localeCompare(b.title, 'pt-BR'));
  }
  return copy.sort((a, b) => Date.parse(b.addedAt) - Date.parse(a.addedAt));
}
